import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import RedesignPage from "@/redesign/RedesignPage";
import LoadBoundary from "@/components/LoadBoundary";
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminSetup = lazy(() => import("@/pages/AdminSetup"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminGallery = lazy(() => import("@/pages/AdminGallery"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminTestimonials = lazy(() => import("@/pages/AdminTestimonials"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminEmails = lazy(() => import("@/pages/AdminEmails"));
const AdminPassword = lazy(() => import("@/pages/AdminPassword"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminLayout = lazy(() => import("@/components/AdminLayout"));
import { Toaster } from "@/components/ui/sonner";

import { API } from "@/lib/api";
export { API } from "@/lib/api";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Scroll to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (pathname === '/admin' || pathname.startsWith('/admin/')) {
      ['robots', 'googlebot'].forEach(name => {
        let tag = document.head.querySelector(`meta[name="${name}"]`);
        if (!tag) { tag = document.createElement('meta'); tag.name = name; document.head.appendChild(tag); }
        tag.content = 'noindex, nofollow';
      });
    }
    if (window.location.hash) requestAnimationFrame(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView());
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setAdmin(res.data)).catch(() => {
        setToken(null);
        setAdmin(null);
        localStorage.removeItem("admin_token");
      });
    }
  }, [token]);

  const login = (newToken, adminData) => {
    setToken(newToken);
    setAdmin(adminData);
    localStorage.setItem("admin_token", newToken);
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem("admin_token");
  };

  return (
    <MotionConfig reducedMotion="user">
    <AuthContext.Provider value={{ token, admin, login, logout }}>
      <BrowserRouter>
        <ScrollToTop />
        <LoadBoundary>
        <Suspense fallback={<p role="status" className="content-status">Loading page…</p>}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute>
              <AdminLayout><AdminGallery /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/blog" element={
            <ProtectedRoute>
              <AdminLayout><AdminBlog /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/testimonials" element={
            <ProtectedRoute>
              <AdminLayout><AdminTestimonials /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedRoute>
              <AdminLayout><AdminLeads /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/emails" element={
            <ProtectedRoute>
              <AdminLayout><AdminEmails /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/password" element={
            <ProtectedRoute>
              <AdminLayout><AdminPassword /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute>
              <AdminLayout><AdminSettings /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="*" element={<RedesignPage />} />
        </Routes>
        </Suspense>
        </LoadBoundary>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
    </MotionConfig>
  );
}

export default App;
